/**
 * What the roads on a route are: their speed limits and how much they rank,
 * from OpenStreetMap's tags — and what that means at each junction.
 *
 * OSRM routes say which OSM nodes they pass through; Overpass says which ways
 * those nodes are on, with `highway=*` and `maxspeed=*`. From those the car
 * drives at the posted limit — the Swedish default for the road's kind where
 * none is posted — and knows at each turn whether it is joining a bigger road
 * (stop at the line), turning off onto a smaller one (slow, and go), or just
 * using a slip road on or off a motorway (neither).
 *
 * Pure: the fetching is `loaders/routeService.ts`.
 */

/** A road's class, most important first; links rank with what they lead to, a little under. */
const RANK: Record<string, number> = {
  motorway: 8, trunk: 7, primary: 6, secondary: 5, tertiary: 4,
  unclassified: 3, residential: 2, living_street: 1, service: 1, track: 0.5,
};

/** How a road ranks for who gives way: 0 for anything unknown. */
export function roadRank(highway: string | undefined): number {
  if (highway === undefined) return 0;
  const link = highway.endsWith('_link');
  const r = RANK[link ? highway.slice(0, -5) : highway] ?? 0;
  return link ? r - 0.5 : r;
}

/**
 * The limit where none is posted, km/h: Sweden's by the road's kind — 50 in
 * town, 70 to 90 on the main roads out, 110 on a motorway, walking pace in a
 * living street.
 */
const DEFAULT_KMH: Record<string, number> = {
  motorway: 110, motorway_link: 70, trunk: 90, trunk_link: 60, primary: 80, primary_link: 50,
  secondary: 70, secondary_link: 50, tertiary: 60, tertiary_link: 40, unclassified: 60,
  residential: 50, living_street: 7, service: 20, track: 30,
};

/** `maxspeed=*` as km/h: "50", "30 mph", "SE:urban", "none"... Undefined when it cannot be read. */
export function parseMaxspeed(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const v = raw.trim().toLowerCase();
  const n = Number.parseFloat(v);
  if (Number.isFinite(n) && n > 0) return v.includes('mph') ? n * 1.609 : v.includes('knots') ? n * 1.852 : n;
  if (v === 'walk' || v.endsWith(':living_street')) return 7;
  if (v.endsWith(':urban')) return 50;
  if (v.endsWith(':rural')) return 70;
  if (v.endsWith(':motorway')) return 110;
  if (v.endsWith(':trunk') || v.endsWith(':expressway')) return 90;
  if (v === 'none') return 130;
  return undefined;
}

/** The limit on a road, m/s: posted, else the default for its kind. */
export function limitOf(highway: string | undefined, maxspeed: string | undefined): number | undefined {
  const posted = parseMaxspeed(maxspeed);
  const kmh = posted ?? (highway === undefined ? undefined : DEFAULT_KMH[highway]);
  return kmh === undefined ? undefined : kmh / 3.6;
}

/** The limits Swedish roads are signed with, km/h. */
const SIGNED = [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];
/**
 * The router drives a road at 80% of its limit, to allow for traffic (OSRM's
 * car profile). Its speed divided back, to the nearest signed limit, is the
 * limit — near enough for when OSM itself cannot be asked.
 */
const ROUTER_SHARE = 0.8;

/** The posted limit a router speed implies, m/s; undefined for a crawl, which says nothing. */
export function limitFromRouter(speed: number): number | undefined {
  if (!(speed > 2)) return undefined;
  const kmh = (speed * 3.6) / ROUTER_SHARE;
  let best = SIGNED[0];
  for (const s of SIGNED) if (Math.abs(s - kmh) < Math.abs(best - kmh)) best = s;
  return best / 3.6;
}

/** What the car does at a junction the route turns at. */
export type Give = 'stop' | 'yield' | 'none';

/**
 * At a maneuver, what the car does, from its type and the rank of the road
 * it leaves and the one it takes. Slip roads on and off, merges and forks:
 * nothing. Roundabouts: give way. Joining a bigger road, an equal one, or at
 * a road's end: stop at the line. Turning off onto a smaller road: slow only.
 */
export function giveAt(type: string, modifier: string | undefined, from: number, to: number): Give {
  if (type === 'on ramp' || type === 'off ramp' || type === 'merge' || type === 'fork' ||
    type === 'continue' || type === 'new name' || type === 'use lane' || type === 'notification' ||
    type === 'depart' || type === 'arrive') return 'none';
  if (type === 'roundabout' || type === 'rotary' || type === 'roundabout turn' ||
    type === 'exit roundabout' || type === 'exit rotary') return 'yield';
  if (modifier === 'straight') return from > 0 && to > from ? 'stop' : 'none';
  if (type === 'end of road') return 'stop';
  if (type === 'turn') {
    // Without the roads' classes, a turn is a stop, as before.
    if (from === 0 || to === 0) return 'stop';
    return to < from ? 'yield' : 'stop';
  }
  return 'none';
}
